import * as React from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import { isEurope } from "../utils/isEurope";

type UserFormProps = {
  userData: {
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    place_of_birth: string;
    nationality: string;
    country_residence: string;
    employment_title: string;
    employment_type: string;
    address1: string;
    city: string;
    state: string;
    country: string;
    department: string;
    location: string;
    token: string | null;
    company_id: string | null;
  };
  errors: {
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    place_of_birth: string;
    nationality: string;
    country_residence: string;
    employment_title: string;
    employment_type: string;
    address1: string;
    city: string;
    state: string;
    country: string;
    department: string;
    location: string;
  };
  setErrors: (value: any) => void;
  setUserData: (value: any) => void;
  steps: string[];
  activeStep: number;
  setActiveStep: (value: number) => void;
  countries: { data: string[] };
  departments: { data: string[] };
  locations: { data: string[] };
  user: {
    department_id: any;
    location_id: any;
  };
  isSsoUser: boolean;
};

export default function UserForm({
  userData,
  errors,
  setErrors,
  setUserData,
  steps,
  activeStep,
  setActiveStep,
  countries,
  departments,
  locations,
  user,
  isSsoUser,
}: UserFormProps) {
  const handleNext = () => {
    const dateOfBirth = new Date(userData.date_of_birth);

    if (userData.first_name.trim() === "") {
      setErrors({ ...errors, first_name: "This field required" });
    } else if (/^[A-Za-z ]*$/.test(userData.first_name) === false) {
      setErrors({ ...errors, first_name: "This field invalid" });
    } else if (userData.last_name.trim() === "") {
      setErrors({ ...errors, last_name: "This field required" });
    } else if (/^[A-Za-z ]*$/.test(userData.last_name) === false) {
      setErrors({ ...errors, last_name: "This field invalid" });
    } else if (userData.gender.trim() === "") {
      setErrors({ ...errors, gender: "This field required" });
    } else if (userData.date_of_birth == null) {
      setErrors({ ...errors, date_of_birth: "This field required" });
    } else if (userData.date_of_birth === "Invalid Date") {
      setErrors({ ...errors, date_of_birth: "Invalid Date" });
    } else if (dateOfBirth.getFullYear() < 1900) {
      setErrors({
        ...errors,
        date_of_birth: "Date of birth must be greater than 1900",
      });
    } else if (userData.place_of_birth === "") {
      setErrors({ ...errors, place_of_birth: "This field required" });
    } else if (userData.nationality === "") {
      setErrors({ ...errors, nationality: "This field required" });
    } else if (userData.country_residence === "") {
      setErrors({ ...errors, country_residence: "This field required" });
    } else if (
      userData.department === "" &&
      departments.data.length &&
      user?.department_id === null
    ) {
      setErrors({ ...errors, department: "This field required" });
    } else if (
      userData.location === "" &&
      locations.data.length &&
      user?.location_id === null
    ) {
      setErrors({ ...errors, location: "This field required" });
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleChange = (name: string, value: string) => {
    setErrors({
      ...errors,
      first_name: "",
      last_name: "",
      gender: "",
      date_of_birth: null,
      nationality: "",
      place_of_birth: "",
      country_residence: "",
      department: "",
      location: "",
    });
    setUserData({ ...userData, [name]: value });
  };

  const error_message_text = (errors: any, field: any) => {
    const error_message = isEurope ? "" : "Must match Emirates ID";
    return errors[field] !== "" ? errors[field] : error_message;
  };

  return (
    <>
      {!isEurope ? (
        <p
          style={{
            color: "grey",
            textAlign: "center",
            fontStyle: "italic",
            fontSize: 16,
          }}
        >
          Please ensure the first and last name and DOB are matching your
          Emirates ID exactly.
        </p>
      ) : (
        <> </>
      )}
      <Typography variant="h6" gutterBottom>
        Basic Info
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="firstName"
            name="first_name"
            label="First name"
            fullWidth
            autoComplete="given-name"
            error={errors.first_name !== ""}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={userData.first_name}
            helperText={error_message_text(errors, "first_name")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            required
            id="lastName"
            name="last_name"
            label="Last name"
            fullWidth
            autoComplete="family-name"
            error={errors.last_name !== ""}
            onChange={(e) => handleChange(e.target.name, e.target.value)}
            value={userData.last_name}
            helperText={error_message_text(errors, "last_name")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl
            sx={{ m: 0, minWidth: "100%" }}
            error={errors.gender !== ""}
          >
            <InputLabel id="demo-simple-select-required-label">
              Gender *
            </InputLabel>
            <Select
              required
              labelId="demo-simple-select-standard-label"
              id="gender"
              label="Gender "
              name="gender"
              error={errors.gender !== ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={userData.gender}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="MALE">MALE</MenuItem>
              <MenuItem value="FEMALE">FEMALE</MenuItem>
            </Select>
            <FormHelperText>
              {errors.gender !== "" ? errors.gender : ""}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl sx={{ m: 0, minWidth: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date of birth *"
                inputFormat="dd/MM/yyyy"
                value={userData.date_of_birth}
                onChange={(e) => handleChange("date_of_birth", e)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errors.date_of_birth != null}
                    helperText={
                      errors.date_of_birth != null ? errors.date_of_birth : ""
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl
            sx={{ m: 0, minWidth: "100%" }}
            error={errors.place_of_birth !== ""}
          >
            <InputLabel id="demo-simple-select-required-label">
              Place of birth *
            </InputLabel>
            <Select
              required
              labelId="demo-simple-select-standard-label"
              name="place_of_birth"
              id="place_of_birth"
              style={{ width: 239 }}
              error={errors.place_of_birth !== ""}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={userData.place_of_birth}
              label="Place of birth"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countries.data.map((value: string) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.place_of_birth !== "" ? errors.place_of_birth : ""}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl
            sx={{ m: 0, minWidth: "100%" }}
            error={errors.nationality !== ""}
          >
            <InputLabel id="demo-simple-select-required-label">
              Nationality *
            </InputLabel>
            <Select
              required
              name="nationality"
              labelId="demo-simple-select-standard-label"
              id="nationality"
              style={{ width: 239 }}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={userData.nationality}
              label="Nationality"
              error={errors.nationality !== ""}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countries.data.map((value: string) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.nationality !== "" ? errors.nationality : ""}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl
            sx={{ m: 0, minWidth: "100%" }}
            error={errors.country_residence !== ""}
          >
            <InputLabel id="demo-simple-select-required-label">
              Country residence *
            </InputLabel>
            <Select
              required
              name="country_residence"
              labelId="demo-simple-select-standard-label"
              id="country_residence"
              style={{ width: 239 }}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              value={userData.country_residence}
              label="Country residence"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {countries.data.map((value: string) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.country_residence !== "" ? errors.country_residence : ""}
            </FormHelperText>
          </FormControl>
        </Grid>
        {/* eslint-disable-next-line no-nested-ternary */}
        {departments?.data?.length ? (
          user?.department_id === null ? (
            <Grid item xs={12} sm={6}>
              <FormControl
                sx={{ m: 0, minWidth: "100%" }}
                error={errors.department !== ""}
              >
                <InputLabel id="demo-simple-select-required-label">
                  Department *
                </InputLabel>
                <Select
                  name="department"
                  labelId="demo-simple-select-standard-label"
                  id="department"
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  value={userData.department}
                  label="department"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {departments.data.map((value: string) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.department !== "" ? errors.department : ""}
                </FormHelperText>
              </FormControl>
            </Grid>
          ) : (
            <> </>
          )
        ) : (
          <> </>
        )}
        {/* eslint-disable-next-line no-nested-ternary */}
        {locations?.data?.length ? (
          user?.location_id === null ? (
            <Grid item xs={12} sm={6}>
              <FormControl
                sx={{ m: 0, minWidth: "100%" }}
                error={errors.location !== ""}
              >
                <InputLabel id="demo-simple-select-required-label">
                  Location *
                </InputLabel>
                <Select
                  name="location"
                  labelId="demo-simple-select-standard-label"
                  id="location"
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  value={userData.location}
                  label="location"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {locations.data.map((value: string) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {errors.location !== "" ? errors.location : ""}
                </FormHelperText>
              </FormControl>
            </Grid>
          ) : (
            <> </>
          )
        ) : (
          <> </>
        )}
      </Grid>
      {activeStep === steps.length ? (
        <Typography variant="h5" gutterBottom>
          Thank you for your submission.
        </Typography>
      ) : (
        <>
          {/* {getStepContent(activeStep)} */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            {activeStep !== 0 && !isSsoUser && (
              <Button onClick={handleBack} sx={{ mt: 3, ml: 1 }}>
                Back
              </Button>
            )}

            <Button
              name="next"
              variant="contained"
              onClick={handleNext}
              sx={{ mt: 3, ml: 1 }}
            >
              {activeStep === steps.length - 1 ? "Update" : "Next"}
            </Button>
          </Box>
        </>
      )}
    </>
  );
}
